import { useEffect, useState } from 'react'

/*
  Terms and Conditions page.
  - No Figma design — layout follows the MIG style guide:
      - h1-title page (180px alfarn-2) for the page heading
      - h2-large (48px alfarn-2) for each section heading (auto-numbered: "1.")
      - body-default (24px Poppins light) for body paragraphs
      - Numbered sub-clauses rendered as "1.1", "1.2" etc. with a fixed
        number column on the left
      - Lettered sub-bullets within a clause rendered as "(a)", "(b)" etc.
      - 1px black divider between major sections
  - Content sourced verbatim from the client-supplied Google Doc
    (Teamwork task 26083933). Static, hardcoded — no CMS data bridge.
  - Font sizes are inline per project convention (beats the #app reset
    specificity); class names carry the responsive overrides.
*/

/* ---- Lightweight inline node so we can mix text and links inside a clause ---- */
type Inline = string | { kind: 'link'; href: string; text: string }

interface NumberedItem {
  /** Plain text body. Used unless `rich` is set. */
  text?: string
  /** Rich body with mixed text and inline links. Used in place of `text`. */
  rich?: Inline[]
  /** Lettered sub-bullets: rendered as (a), (b), (c) ... */
  subList?: string[]
}

interface Section {
  heading: string
  /**
   * Sections that have a single non-numbered prose paragraph (e.g. Online
   * Purchases, Variation, Advertisements, Territorial Restriction, Security
   * of Information) use `intro`. Sections with multiple numbered clauses
   * use `items`. A section never has both.
   */
  intro?: string
  items?: NumberedItem[]
}

const INTRO_PARAGRAPH =
  'Willowcharm Pty Ltd as Trustee of Da Silva Family Trust (ACN 139 390 846) and its related companies (together we, us, our and other similar expressions) welcome you, the viewer and user of this website.'

const SECTIONS: Section[] = [
  {
    heading: 'Acceptance of these Terms of Use',
    items: [
      { text: 'We provide this website subject to these terms of use. Before you browse or use any facilities on this website, it is important that you read, understand, and agree to these terms of use.' },
      { text: 'You must not use this website if you are not of a legal age or capacity to form a binding contract with us.' },
      { text: 'By using this website, you agree to be bound by these terms of use. If you use this website on behalf of a third party such as an organisation or employer, you agree to these terms of use on your own behalf and on behalf of that third party. If you do not accept these terms of use, you must not use this website.' },
    ],
  },
  {
    heading: 'Access',
    items: [
      { text: 'You must ensure that your access to this website is not illegal or prohibited by laws that apply to you.' },
      { text: 'We may terminate or suspend your access to this website at any time without notice to you.' },
    ],
  },
  {
    heading: 'Privacy',
    items: [
      {
        rich: [
          'We treat all personal information submitted to us in accordance with our Privacy Policy. For more information about our Privacy Policy, please ',
          { kind: 'link', href: '/privacy-policy', text: 'click here' },
          '.',
        ],
      },
    ],
  },
  {
    heading: 'Online Purchases',
    intro: 'All purchases made through this website will be subject to our terms and conditions of trade set out here.',
  },
  {
    heading: 'Variation',
    intro: 'We may amend these terms of use from time to time. Amendments will be effective immediately following publication of the amended terms on this website. You agree to periodically check this website to ensure that you understand the requirements of the current terms of use and that your continued use of this website will represent an agreement by you to be bound by the terms of use as amended.',
  },
  {
    heading: 'Registration of Account',
    items: [
      { text: 'In order to access certain parts of this website or particular content, you must register for an account.' },
      { text: 'You must enter account information which is true and correct.' },
      { text: 'You must keep your password and account details safe and secure.' },
      { text: 'You are responsible for all use of this website, including the ordering of products and all fees and charges in connection with that use, which occurs using an account registered in your name.' },
      { text: 'We may suspend or delete any registered account in our absolute discretion.' },
    ],
  },
  {
    heading: 'Restricted Use',
    items: [
      {
        text: 'You are authorised to reproduce any content available on or accessible from this website (Content) for the purposes of:',
        subList: [
          'placing an order for products with us;',
          'using this website as a technical resource; or',
          'for ancillary purposes in connection with the purposes set out in clauses 7.1(a) and 7.1(b).',
        ],
      },
      { text: 'You must not publish or sell any part of the Content or grant others access to the Content for a fee without our prior written approval.' },
      { text: 'You agree to use the Content and this website for lawful purposes only.' },
      { text: 'Except where otherwise permitted by these terms, you must not modify, copy, reproduce, republish, upload, post, transmit or distribute in any way any material from this website including code and software.' },
      { text: 'You must not use data mining, robots, screen scraping or similar data gathering and extraction tools on this website for establishing, maintaining, advancing or reproducing information contained on our website, on your own website or in any other publication, or for your own personal use or benefit (other than as expressly set out in these terms of use), except with our prior written consent.' },
    ],
  },
  {
    heading: 'Content',
    items: [
      { text: 'All Content is provided by us or by others in good faith. You accept that the Content provided by us is general information and is not advice. We have derived the Content from sources which we believe to be accurate and up-to-date as at the date of publication.' },
      { text: 'We make no representation about the accuracy, completeness or usefulness of the Content or its fitness for any particular purposes, nor do we undertake to keep this website or the Content up-to-date. This applies to Content provided by us and to Content provided by others.' },
      { text: 'Where the Content provided by others contains opinions or judgements of third parties, we do not endorse those opinions or judgements, nor their accuracy or reliability.' },
      {
        text: 'You must evaluate and bear all risks associated with the use of Content, whether provided by us or by others, including your reliance on the accuracy, completeness or usefulness of it. By using this website, you agree that neither we nor our officers, employees and agents are responsible for:',
        subList: [
          'the accuracy of the content displayed on or omitted from the website;',
          "any person's reliance on content available on or omitted from the website; or",
          'any loss in connection with the use of the website.',
        ],
      },
      { text: 'You should make your own enquiries and seek independent advice from relevant industry professionals before acting or relying on any Content, whether provided by us or by others.' },
    ],
  },
  {
    heading: 'Advertisements',
    intro: "Responsibility for the content of advertisements on this website (including hyperlinks to the advertiser's own websites) rests solely with the advertisers. The placement of advertisements on this website does not constitute a recommendation or endorsement by us of the advertiser's products and each advertiser is solely responsible for any representations made in connection with its advertisement.",
  },
  {
    heading: 'Copyright',
    items: [
      { text: 'Copyright in the Content and this website (including text, graphics, logos, icons, sound recordings and software) is owned or licensed by us.' },
      {
        text: 'Other than for the purposes of, and subject to the conditions prescribed under, the Copyright Act 1968 (Cth) and similar legislation which applies in your location, and except as expressly authorised by these terms of use, you may not in any form or by any means:',
        subList: [
          'adapt, reproduce, distribute, print, display, perform, publish or create derivative works from any part of this website; or',
          'use any Content, goods or services obtained from any part of this website for commercial purposes, without our prior written approval.',
        ],
      },
    ],
  },
  {
    heading: 'Trademarks',
    items: [
      { text: 'If any of our trademarks are displayed on this website, you must not use them without our prior written approval.' },
      { text: 'This website may also display names and logos that are trademarks of third parties. You must not use those trademarks without our prior written approval or the prior written approval of the relevant third-party owner.' },
    ],
  },
  {
    heading: 'Territorial Restriction',
    intro: 'We make no representation that the Content is appropriate or available for use outside Australia. If you choose to access this website from other locations, you do so at your own risk and on your own initiative, and you are responsible for compliance with any applicable local laws.',
  },
  {
    heading: 'Communication With Us and Others',
    items: [
      { text: 'This website may contain communication facilities which allow you to communicate with us and others through online enquiry and feedback forms, electronic messaging, blog, forum, chat room or notice services (Communication Facilities).' },
      { text: 'You acknowledge that Communications Facilities may be public and not private communications and that Communication Facilities are not reviewed, screened or approved by us.' },
      {
        text: 'When using the Communication Facilities or this website, you must not, and you must not authorise, assist or encourage any other person to, post or transmit any information, image, text or other material that:',
        subList: [
          'is not original material in which you own copyright, unless you are authorised by the copyright owner to post or transmit that material on this website;',
          'violates or infringes the rights of any other person;',
          'contains personal information of any other person, such as phone numbers, addresses or email addresses;',
          'is unlawful, threatening, abusive, defamatory, obscene, profane or pornographic;',
          'vilifies, insults or humiliates any person on the basis of race, religion, ethnicity, gender, age, sexual orientation or any physical or mental disability;',
          'contains a virus or other harmful component;',
          'advertises or promotes any goods or services, websites, competitions or schemes or is otherwise for commercial purposes; or',
          'is knowingly incorrect, misleading or deceptive.',
        ],
      },
      { text: 'In addition, you must not use the Communications Facilities in a manner which may hinder or prevent us from providing services to any other person or which may threaten the integrity or use by any person of this website.' },
      { text: 'Responsibility for the content of material posted on this website rests solely with the person who posts it. Where that content contains opinions or judgements of third parties, we do not endorse those opinions or judgements, nor their accuracy or reliability.' },
    ],
  },
  {
    heading: 'User Licence to Us',
    items: [
      {
        text: 'By submitting any information or other material to us (including inputting data or engaging in any other form of communication), you grant us a perpetual, royalty-free, non-exclusive, irrevocable, unrestricted, worldwide licence to do all or any of the following in respect of the information or material:',
        subList: [
          'use, copy, sublicense, redistribute, edit, adapt, transmit, publish, broadcast, publicly perform, communicate to the public or display; and',
          'sublicense to any third parties the unrestricted right to exercise any of the rights granted in clause 14.1(a).',
        ],
      },
      { text: 'The licence in clause 14.1 includes the right to exploit all proprietary rights in that information or other material including but not limited to rights under copyright, trademark, service mark or patent laws under any jurisdiction worldwide.' },
      { text: "You unconditionally and irrevocably consent to all acts and omissions by us, or people authorised by us, which would otherwise amount to an infringement of your moral rights in that information or other material. This includes consent to change the information or other material even if the change amounts to a 'derogatory treatment' of the information or other material as that term is defined in Division 4 of Part 9 of the Copyright Act 1968 (Cth)." },
      { text: 'At our request and expense, you must execute and deliver to us any instruments and take any other actions required to give full legal effect to this grant of licence and consent.' },
    ],
  },
  {
    heading: 'Viruses',
    items: [
      { text: 'You must take your own precautions to ensure that the process which you use for accessing this website does not expose you to the risk of viruses, malicious computer code or other forms of interference which may damage your own computer system.' },
      { text: 'We do not accept responsibility for any interference or damage to your own computer system which arises in connection with your use of this website or any linked website.' },
    ],
  },
  {
    heading: 'Security of Information',
    intro: 'Unfortunately, no data transmission over the internet can be guaranteed as totally secure. While we strive to protect information we transmit and receive, we do not warrant and cannot assure the security of any information which you transmit to us.',
  },
  {
    heading: 'Linked Sites',
    items: [
      { text: 'This website may be linked to other websites over which we have no control. Those links are provided for convenience only and may not remain current or be maintained.' },
      { text: 'We do not sponsor, endorse, adopt, confirm, guarantee or approve the content or representations made on those websites. We are not liable for and make no representations about the accuracy of content on those websites.' },
      { text: 'No links from external websites to this website are permitted without our prior written approval.' },
    ],
  },
  {
    heading: 'Limitation of Liability',
    items: [
      { text: 'Nothing in these terms of use is or should be interpreted as an attempt to modify, limit or exclude any right or remedy, or any guarantee, term, condition, warranty, undertaking, inducement or representation, implied or imposed by legislation which cannot be modified, limited or excluded.' },
      { text: 'We exclude to the extent permitted by law all other guarantees, terms, conditions, warranties, undertakings, inducements or representations whether express or implied, statutory or otherwise, which would otherwise be implied into these terms of use concerning the activities covered by these terms of use.' },
      { text: 'You agree that you do not rely on any guarantee, term, condition, warranty, undertaking, inducement or representation made by us or on our behalf which is not expressly stated in these terms of use. In particular, we make no warranty or representation that this website will always be available, accessible, secure or operate without error.' },
      {
        text: 'Where any legislation implies or imposes a guarantee, term, condition, warranty, undertaking, inducement or representation in relation to these terms of use and we are not able to exclude that guarantee, term, condition, warranty, undertaking, inducement or representation (Non-Excludable Provision), and we are able to limit your remedy for a breach of the Non-Excludable Provision, then our liability for breach of the Non-Excludable Provision is limited to (at our election):',
        subList: [
          'in the case of goods, the repair or replacement of the goods or the supply of equivalent goods (or the payment of the cost of doing so); and',
          'in the case of services, the supplying of the services again (or the payment of the cost of doing so).',
        ],
      },
      {
        text: 'Subject to our obligations under the Non-Excludable Provisions:',
        subList: [
          'we do not accept responsibility for any loss or damage, however caused (including through negligence), which you may directly or indirectly suffer in connection with your use of this website or any linked website, nor do we accept any responsibility for any loss arising out of your use of or reliance on Content; and',
          'we are not liable to you or any third party for any economic loss, loss of actual or anticipated profits, loss of revenue, loss of savings, loss of production, loss of business, loss of opportunity, loss of access to markets, loss of goodwill, loss of reputation, loss of credit, loss of publicity, loss of data, loss of use, loss of interest or loss arising from business interruption or any special, indirect, consequential, incidental, punitive, exemplary or unforeseeable loss or any similar loss arising in connection with any use or access, or any inability to use or access, or misuse by you or any other party, of this website or otherwise in connection with any Content, whether arising in or for breach of contract, negligence or other tort, breach of statutory duty, breach of warranty or guarantee, under an indemnity or otherwise and whether or not that loss was foreseeable, even if we have been advised of the possibility of such loss.',
        ],
      },
    ],
  },
  {
    heading: 'Indemnity',
    items: [
      { text: 'You agree to hold harmless and indemnify us and our officers, agents and employees against any loss, damage, costs or expenses that we, or any of our officers, agents and employees, may incur in connection with your breach of these terms of use or any other legal obligation, your use of this website or any use of this website using an account registered in your name.' },
      {
        text: 'The indemnity in this clause:',
        subList: [
          'is a continuing obligation, separate and independent from the other obligations of the parties;',
          'will not be affected by any matter including without limitation, the termination, renewal or extension of these terms of use or any indulgence, waiver or other concession given by us unless we agree in writing; and',
          'includes legal costs and disbursements on a full indemnity basis.',
        ],
      },
      { text: 'It is not necessary for us to incur expense or to make any payment before enforcing the right of indemnity conferred by this clause.' },
      { text: 'You must pay on demand any amount you must pay under the indemnity in this clause.' },
    ],
  },
  {
    heading: 'General',
    items: [
      { text: 'Headings in these terms of use are for convenience only and do not affect interpretation.' },
      { text: 'A reference to a statute or other law includes regulations and other instruments under it and consolidations, amendments, re-enactments or replacements of any of them.' },
      { text: 'Except as expressly provided to the contrary in these terms of use, we may give or withhold an approval or consent in our absolute discretion and subject to any conditions determined by us. We are not obliged to give our reasons for giving or withholding a consent or approval or for giving a consent or approval subject to conditions.' },
      { text: 'None of your rights and obligations under these terms of use may be assigned or transferred without our prior written consent.' },
      { text: 'Except as expressly provided to the contrary in these terms of use, nothing in these terms of use or the use of this website will constitute you and us as principal and agent, employer and employee, partners or otherwise liable for the acts or omissions of each other.' },
      { text: 'These terms of use record the entire agreement between you and us in relation to your use of this website. They supersede all prior contracts, arrangements, understandings or negotiations by, or between, you and us in relation to your use of this website. You may also be subject to additional terms and conditions that apply when you use particular services available on or through this website.' },
      { text: 'You must at your own expense do all things that we reasonably require of you to give us the full benefit of any obligations owed to us and expressed in these terms of use.' },
      { text: 'All warranties, releases, exclusions and limitations of liability, indemnities, terms with respect to intellectual property and confidential information in these terms of use will remain valid and binding following expiry or termination of these terms of use. Any other provision by its nature intended to survive expiry or termination of these terms of use survives expiry or termination of these terms of use.' },
      { text: 'The failure, delay or omission by us to exercise, or to partially exercise, a right, power or remedy under these terms of use (or any connected agreement) does not operate as a waiver of that right, power or remedy. If we exercise, or partially exercise, a right, power or remedy, we maintain our right to further exercise the same right, power or remedy or to exercise another right, power or remedy. We only waive a right, power or remedy by explicitly doing so in a written notice to you and the waiver is strictly limited to the matters specified in the notice.' },
      { text: 'Our rights, powers, authorities, discretions and remedies under these terms of use do not exclude any other right, power, authority, discretion or remedy.' },
      {
        text: 'If any provision of these terms of use is determined by a court or other competent tribunal or authority to be illegal, invalid or unenforceable then:',
        subList: [
          'where the offending provision can be read down so as to give it a legal, valid and enforceable operation of a partial nature it must be read down to the extent necessary to achieve that result;',
          'where the offending provision cannot be read down then that provision must be severed from these terms of use in which event, the remaining provisions of these terms of use operate as if the severed provision had not been included; and',
          'the legality, validity or enforceability of that provision in any other jurisdiction or of the remaining provisions in that or any other jurisdiction is not affected, but only to the extent that is consistent with giving substantial effect to the intentions of the parties under these terms of use.',
        ],
      },
      { text: 'These terms of use are governed by the law of New South Wales, Australia. Each party submits to the jurisdiction of the courts in New South Wales, Australia in connection with matters concerning these terms of use.' },
    ],
  },
]

/* ---- Render rich inline nodes (text + optional links) ---- */
function RichText({ nodes }: { nodes: Inline[] }) {
  return (
    <>
      {nodes.map((node, i) =>
        typeof node === 'string' ? (
          <span key={i}>{node}</span>
        ) : (
          <a key={i} href={node.href} className="mig-terms-rich-link">{node.text}</a>
        )
      )}
    </>
  )
}

/* ---- A single numbered clause: "1.1 ..." with an optional lettered sub-list ---- */
function Clause({
  number,
  item,
}: {
  number: string
  item: NumberedItem
}) {
  return (
    <div className="mig-terms-clause">
      <span className="mig-terms-clause-number">{number}</span>

      <div className="mig-terms-clause-body">
        <p className="mig-terms-paragraph">
          {item.rich ? <RichText nodes={item.rich} /> : item.text}
        </p>

        {item.subList && item.subList.length > 0 && (
          <div className="mig-terms-sublist">
            {item.subList.map((subItem, idx) => (
              <div key={idx} className="mig-terms-subitem">
                <span className="mig-terms-subitem-letter">
                  ({String.fromCharCode(97 + idx)})
                </span>
                <span className="mig-terms-subitem-text">{subItem}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default function TermsAndConditions() {
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100)
    return () => clearTimeout(timer)
  }, [])

  return (
    <section className="mig-page-section">
      <div className="mig-page-title-row" data-visible={visible}>
        <h1 className="mig-terms-page-title">Terms of Use</h1>
      </div>

      <div className="mig-terms-content">
        <p className="mig-terms-paragraph">{INTRO_PARAGRAPH}</p>

        {SECTIONS.map((section, sIdx) => {
          const sectionNumber = sIdx + 1
          return (
            <article key={section.heading} className="mig-terms-section">
              <h2 className="mig-terms-section-heading">
                {`${sectionNumber}. ${section.heading}`}
              </h2>

              {section.intro && (
                <p className="mig-terms-paragraph">{section.intro}</p>
              )}

              {section.items && section.items.length === 1 && (
                /* Single-clause sections (e.g. Privacy) render the body
                   without a "X.1" prefix — there's nothing to number against.
                   Same visual treatment as `intro`-style sections (4. Online
                   Purchases, 5. Variation), but RichText preserves embedded
                   links the `intro` string can't carry. */
                <>
                  <p className="mig-terms-paragraph">
                    {section.items[0].rich
                      ? <RichText nodes={section.items[0].rich} />
                      : section.items[0].text}
                  </p>
                  {section.items[0].subList && section.items[0].subList.length > 0 && (
                    <div className="mig-terms-sublist">
                      {section.items[0].subList.map((subItem, idx) => (
                        <div key={idx} className="mig-terms-subitem">
                          <span className="mig-terms-sublist-letter">{`(${String.fromCharCode(97 + idx)})`}</span>
                          <p className="mig-terms-paragraph">{subItem}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {section.items && section.items.length > 1 && (
                <div className="mig-terms-clause-list">
                  {section.items.map((item, iIdx) => (
                    <Clause
                      key={iIdx}
                      number={`${sectionNumber}.${iIdx + 1}`}
                      item={item}
                    />
                  ))}
                </div>
              )}

              {sIdx < SECTIONS.length - 1 && (
                <div className="mig-terms-section-divider">
                  <div className="mig-divider-line" />
                </div>
              )}
            </article>
          )
        })}
      </div>
    </section>
  )
}
